import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { shopApi } from "./ShopContext";
import { ProductCard } from "./ProductCard";

const SORTS = [["name", "A – Z"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"], ["newest", "Newest"]];

export default function ShopCatalog() {
  const [params, setParams] = useSearchParams();
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const search = params.get("search") || "";
  const department = params.get("department") || "";
  const sort = params.get("sort") || "name";
  const page = Number(params.get("page") || 1);

  const set = (k, v) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== "page") n.delete("page"); setParams(n); };

  useEffect(() => { shopApi.get("/departments").then(({ data: d }) => setDepartments(d)).catch(() => {}); }, []);
  useEffect(() => {
    setData(null); setFailed(false);
    const params = { search: search || undefined, department: department || undefined, sort, page, limit: 24 };
    const get = () => shopApi.get("/products", { params, timeout: 20000 });
    // One quiet retry: a brief server hiccup shouldn't look like "no results".
    get().catch(() => new Promise((r) => setTimeout(r, 1500)).then(get))
      .then(({ data: d }) => setData(d))
      .catch((e) => { console.error("Shop search failed", e); setFailed(true); setData({ products: [], total: 0, pages: 0 }); });
  }, [search, department, sort, page, attempt]);

  return (
    <div className="flex flex-col gap-6 lg:flex-row" data-testid="shop-catalog">
      <aside className="lg:w-56 lg:shrink-0">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Departments</p>
        <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible">
          {[{ name: "" }, ...departments].map((d) => (
            <button key={d.name || "all"} type="button" onClick={() => set("department", d.name)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-left text-sm lg:rounded-lg ${department === d.name ? "bg-[#D96B27] font-bold text-white" : "bg-white text-slate-700 hover:bg-[#FEF3EC]"}`}
              data-testid={`shop-filter-dept-${d.name || "all"}`}>
              {d.name || "All departments"}
            </button>
          ))}
        </div>
      </aside>
      <div className="flex-1">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="shop-catalog-title">
            {search ? `Results for "${search}"` : department || "All products"}
            {data && !failed && <span className="ml-2 text-sm font-normal text-slate-500">({data.total})</span>}
          </h1>
          <select value={sort} onChange={(e) => set("sort", e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="shop-sort-select">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {!data ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#D96B27]" /></div>
        ) : failed ? (
          <div className="py-20 text-center" data-testid="shop-search-failed">
            <p className="text-slate-600">We couldn't load products just now.</p>
            <button type="button" onClick={() => setAttempt((a) => a + 1)} className="mt-3 rounded-full bg-[#D96B27] px-5 py-2 text-sm font-bold text-white" data-testid="shop-search-retry">Try again</button>
          </div>
        ) : data.products.length === 0 ? (
          <p className="py-20 text-center text-slate-500" data-testid="shop-no-results">No products found. Try another search.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">{data.products.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        )}
        {data && data.pages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <button type="button" disabled={page <= 1} onClick={() => set("page", String(page - 1))} className="rounded-full border px-4 py-2 text-sm disabled:opacity-40" data-testid="shop-page-prev">Previous</button>
            <span className="text-sm text-slate-600" data-testid="shop-page-info">Page {page} of {data.pages}</span>
            <button type="button" disabled={page >= data.pages} onClick={() => set("page", String(page + 1))} className="rounded-full border px-4 py-2 text-sm disabled:opacity-40" data-testid="shop-page-next">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
